import { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dialog, Field, HStack, TextField } from '@rtkwlf/fenrir-react';
import { faPencil } from '@rtkwlf/pro-solid-svg-icons/faPencil';
import find from 'lodash/find';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useScannerDetails } from '@apis/rendall/queries/use-scanner-details/use-scanner-details';
import { removeLeadingZerosFromIpOctets } from '@packages/utils/ip/ip';

import {
  ipRangeConfig,
  portsConfig,
} from '../../../components/ip-controls-config';
import type {
  AddIpRangeForm,
  ScanExclusionType,
} from '../add-ip-range-form/add-ip-range-form';

type Props = {
  index: number;
  original: ScanExclusionType;
};

export const EditExclusionDialog = ({ index, original }: Props) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const { scannerId = '' } = useParams();
  const { data: hasPortExemptionCapability } = useScannerDetails(scannerId, {
    select: (data) => data.capabilities.includes('port_scan_exemption'),
    refetchOnMount: false,
  });

  const {
    control,
    getValues,
    getFieldState,
    clearErrors,
    trigger,
    register,
    formState: { errors },
    resetField,
  } = useFormContext<AddIpRangeForm>();

  const { update } = useFieldArray({
    control,
    name: 'scanExclusions',
  });

  const handleCloseDialog = () => {
    setIsDisabled(true);
    setIsOpen(false);
  };

  const handleOpenDialog = () => {
    resetField('editExclusion', { defaultValue: { ...original } });

    if (errors.editExclusion) {
      clearErrors('editExclusion');
    }
    setIsOpen(true);
  };

  const handleEdit = async () => {
    const isValid = await trigger('editExclusion');
    if (!isValid) return;

    const { ipRange, ports } = getValues('editExclusion');
    const trimmedIp = removeLeadingZerosFromIpOctets(ipRange).trim();

    const currentScanExclusionEntries = getValues('scanExclusions');

    const foundInScanExclusionEntries = find(currentScanExclusionEntries, {
      ipRange: trimmedIp,
    });

    const noChanges =
      original.ipRange === trimmedIp && original.ports === ports;

    const portsUpdateOnly =
      original.ipRange === trimmedIp && original.ports !== ports;

    if (!foundInScanExclusionEntries || noChanges || portsUpdateOnly) {
      update(index, { ipRange: trimmedIp, ports });

      resetField('newExclusion', {
        defaultValue: { ipRange: '', ports: '' },
        keepDirty: false,
      });
      resetField('editExclusion', {
        defaultValue: { ipRange: '', ports: '' },
        keepDirty: false,
      });

      setIsOpen(false);
      return;
    } else {
      control.setError(
        'editExclusion.ipRange',
        { message: t('scanExclusion.validation.ipAddressNotUnique') },
        { shouldFocus: false },
      );
      return;
    }
  };

  const { onChange: onChangeIpRange, ...restIpRange } = register(
    'editExclusion.ipRange',
    ipRangeConfig,
  );

  const { onChange: onChangePorts, ...restPorts } = register(
    'editExclusion.ports',
    portsConfig,
  );

  return (
    <Dialog.Root isOpen={isOpen}>
      <Dialog.Trigger asChild>
        <Button
          marginLeft="auto"
          variant="tertiary"
          iconLeft={<FontAwesomeIcon icon={faPencil} />}
          onClick={handleOpenDialog}
        >
          {t('actions.edit')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.HeaderTitle>{t('editDialogTitle')}</Dialog.HeaderTitle>
        </Dialog.Header>
        <Dialog.Body>
          <HStack>
            <Field.Root
              appearance={errors.editExclusion?.ipRange ? 'danger' : 'neutral'}
            >
              <Field.Label>{t('addIPControls.ipAddressRange')}</Field.Label>
              <Field.Content>
                <TextField
                  placeholder={t('addIPControls.ipAddressRangePlaceholder')}
                  {...restIpRange}
                  onChange={(e) => {
                    onChangeIpRange(e);
                    setIsDisabled(!getFieldState('editExclusion').isDirty);
                    if (getFieldState('editExclusion.ipRange').error)
                      clearErrors('editExclusion.ipRange');
                  }}
                />
              </Field.Content>
              <Field.Message>
                {errors?.editExclusion?.ipRange?.message}
              </Field.Message>
            </Field.Root>

            {hasPortExemptionCapability && (
              <Field.Root
                appearance={errors.editExclusion?.ports ? 'danger' : 'neutral'}
              >
                <Field.Label>{t('addIPControls.ports')}</Field.Label>
                <Field.Content>
                  <TextField
                    placeholder={t('addIPControls.portsPlaceholder')}
                    {...restPorts}
                    onChange={(e) => {
                      onChangePorts(e);
                      setIsDisabled(!getFieldState('editExclusion').isDirty);
                      if (getFieldState('editExclusion.ports').error)
                        clearErrors('editExclusion.ports');
                    }}
                  />
                </Field.Content>
                <Field.Message>
                  {errors?.editExclusion?.ports?.message}
                </Field.Message>
              </Field.Root>
            )}
          </HStack>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.FooterCancel onClick={handleCloseDialog}>
            {t('actions.discardChanges')}
          </Dialog.FooterCancel>
          <Dialog.FooterAction onClick={handleEdit} isDisabled={isDisabled}>
            {t('actions.saveChanges')}
          </Dialog.FooterAction>
        </Dialog.Footer>
        <Dialog.Close onClick={handleCloseDialog} />
      </Dialog.Content>
    </Dialog.Root>
  );
};
